import { useState, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Building2, User } from 'lucide-react';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { googleAdsAccountsQueryOptions, useSetGoogleAdsCustomerIdMutationOptions } from '@/lib/query-options';
import type { OrganizationDataWithMembership } from '@/lib/types';
import { useToast } from '@/hooks/useToast';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';

interface OrganizationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizations: OrganizationDataWithMembership[];
  selectedOrganization: OrganizationDataWithMembership | null;
  onOrganizationSelect: (organization: OrganizationDataWithMembership) => Promise<void>;
}

export function OrganizationSelectionModal({
  isOpen,
  onClose,
  organizations,
  selectedOrganization,
  onOrganizationSelect
}: OrganizationSelectionModalProps) {
  const [tempSelection, setTempSelection] = useState<string>(selectedOrganization?.slug || '');
  const { data: googleAdsAccounts = [] } = useSuspenseQuery(googleAdsAccountsQueryOptions());
  const { toast } = useToast();
  const googleAdsCustomerMutation = useSetGoogleAdsCustomerIdMutationOptions();
  const queryClient = useQueryClient();

  // Global state atoms
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const setGoogleAdsAccount = useSetAtom(googleAdsAccountAtom);
  const setOrganizationData = useSetAtom(organizationDataAtom);

  // Campaign counts are now included in the organizations data
  const campaignCounts: Record<number, number> = {};
  organizations.forEach(org => {
    if (org.campaign_count !== undefined) {
      campaignCounts[org.id] = org.campaign_count;
    }
  });

  // Update tempSelection when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSelection(selectedOrganization?.slug || '');
    }
  }, [isOpen, selectedOrganization]);

  // Update Google Ads account selection when organization changes
  useEffect(() => {
    if (isOpen) {
      const selectedOrg = organizations.find((org) => org.slug === tempSelection);
      const currentAccountId = selectedOrg?.membership?.last_selected_googleads_account_id;

      // Find and set the corresponding Google Ads account
      if (currentAccountId) {
        const account = googleAdsAccounts.find(acc => acc.googleads_account_id === currentAccountId);
        if (account) {
          setGoogleAdsAccount(account);
        }
      } else {
        setGoogleAdsAccount(null);
      }
    }
  }, [isOpen, tempSelection, organizations, googleAdsAccounts, setGoogleAdsAccount]);


  const handleConfirm = async () => {
    const selectedOrg = organizations.find((org) => org.slug === tempSelection);
    if (selectedOrg) {
      // First switch organization
      await onOrganizationSelect(selectedOrg);

      // Update organization data with the selected Google Ads account
      if (googleAdsAccount) {
        const updatedOrganization = {
          ...selectedOrg,
          membership: {
            ...selectedOrg.membership,
            last_selected_googleads_account_name: googleAdsAccount.name || googleAdsAccount.googleads_account_id,
            last_selected_googleads_account_id: googleAdsAccount.googleads_account_id,
          }
        };
        setOrganizationData(updatedOrganization);

        // Update the Google Ads account selection in the backend if it's different from current
        if (googleAdsAccount.googleads_account_id !== selectedOrganization?.membership?.last_selected_googleads_account_id) {
          try {
            await googleAdsCustomerMutation.mutateAsync({
              googleAdsAccountId: googleAdsAccount.googleads_account_id,
              organizationId: selectedOrg.id
            });
            // Invalidate conversion actions query to refresh data for the new account
            queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
          } catch (error) {
            console.error('Failed to update Google Ads account:', error);
            toast({
              title: 'Error',
              description: 'Failed to update Google Ads account',
              variant: 'destructive',
            });
          }
        }
      } else {
        // Clear Google Ads account selection for organizations with no connected account
        const updatedOrganization = {
          ...selectedOrg,
          membership: {
            ...selectedOrg.membership,
            last_selected_googleads_account_name: null,
            last_selected_googleads_account_id: null,
          }
        };
        setOrganizationData(updatedOrganization);
      }
    }
    onClose();
  };

  const handleCancel = () => {
    setTempSelection(selectedOrganization?.slug || '');

    // Reset Google Ads account to the current organization's selection
    if (selectedOrganization?.membership?.last_selected_googleads_account_id) {
      const account = googleAdsAccounts.find(acc => acc.googleads_account_id === selectedOrganization.membership.last_selected_googleads_account_id);
      if (account) {
        setGoogleAdsAccount(account);
      }
    } else {
      setGoogleAdsAccount(null);
    }

    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Switch Organization</DialogTitle>
          <DialogDescription>
            Choose the organization you want to work with from the list below.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">Organization</label>
            <Select value={tempSelection} onValueChange={setTempSelection}>
              <SelectTrigger className="w-full">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => {
                  const campaignCount = campaignCounts[org.id] ?? 0;
                  return (
                    <SelectItem key={org.slug} value={org.slug} className="cursor-pointer">
                      {org.name || org.slug} ({campaignCount} campaign{campaignCount !== 1 ? 's' : ''})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {googleAdsAccounts.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">Google Ads Account</label>
              <Select
                value={googleAdsAccount?.googleads_account_id || 'none'}
                onValueChange={async (value) => {
                  if (value === 'none') {
                    setGoogleAdsAccount(null);
                  } else {
                    const account = googleAdsAccounts.find(acc => acc.googleads_account_id === value);
                    if (account) {
                      setGoogleAdsAccount(account);
                      
                      // Update the Google Ads account selection in the backend
                      try {
                        await googleAdsCustomerMutation.mutateAsync({
                          googleAdsAccountId: account.googleads_account_id,
                          organizationId: selectedOrganization?.id
                        });
 
                        // Refetch organizations query (campaign counts are now included)
                        await queryClient.refetchQueries({ queryKey: ['organizations'] });
                      } catch (error) {
                        console.error('Failed to update Google Ads account:', error);
                        toast({
                          title: 'Error',
                          description: 'Failed to update Google Ads account',
                          variant: 'destructive',
                        });
                      }
                    }
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select a Google Ads account" />
                </SelectTrigger>
                <SelectContent>
                  {!googleAdsAccount && (
                    <SelectItem value="none" className="cursor-pointer">None</SelectItem>
                  )}
                  {googleAdsAccounts.map((account) => (
                    <SelectItem key={account.googleads_account_id} value={account.googleads_account_id} className="cursor-pointer">
                      {account.name || account.googleads_account_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!tempSelection}>
            Select
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}